import { useCan } from '@features/user';

/**
 * The permission that governs moving a patient's money (gdn-3287).
 *
 * VERIFIED against the backend half, not predicted: the literal below
 * is the serialised enum value on smartscribe-server#4301 at head
 * b6b00dd14, whose own tests assert it appears in the v2 user-info
 * `permissions` array — the exact list useCan reads. Grants ship in the
 * same PR, so the permission and its grants land together rather than
 * leaving a window where the string resolves but nobody holds it.
 *
 * Note it is NOT a fixed role mapping. An org override wins over the
 * default in BOTH directions: off for a role that holds it by default
 * (`{"manage_patient_financials": {"clinician": false}}`), and ON for a
 * role the defaults exclude (`{"supervisor": true}` — asserted on the BE
 * at b6b00dd14, and scoped, so switching supervisor on leaves
 * clinical_staff refused).
 *
 * Two consequences. First, any role may or may not hold this depending on
 * the org, which is precisely why every call site asks this hook instead
 * of inferring authority from the user's role — a role check would be
 * wrong for some org, not merely unfashionable. Second, the support
 * answer for the roles excluded by default is a setting rather than a
 * release: a practice that wants its supervisors collecting can be turned
 * on without shipping anything.
 *
 * Re-verify if that PR is rebased or amended: a permission literal is a
 * cross-repo contract, and an approval on a superseded commit is not an
 * approval of the string that finally ships. (It has now been checked at
 * four heads — 82b3b3f, 375f798, 4d7da259c, b6b00dd14 — and the literal
 * and grants have been stable across all of them.)
 *
 * Who holds it: practice_administrator, billing_administrator, clinician.
 * Deliberately NOT supervisor, clinical_staff or operations_support —
 * supervisor's exclusion is a product decision taken from an option set
 * that included it, not an oversight. Supervisors can use some of these
 * verbs TODAY (the routes that were provider-auth), so they lose them
 * when the backend half ships. That is the intended narrowing; it is
 * recorded here because "a supervisor says the button vanished" is a
 * support question, not a bug report — and one with an answer that does
 * not need a release, per the override note above.
 *
 * Why the guess is expensive rather than merely wrong: useCan is a
 * membership test against a backend-owned list, so a wrong literal fails
 * CLOSED — every money verb across eight surfaces disappears for
 * everyone including administrators, and it reads as a deliberate
 * product decision rather than a typo.
 *
 * Deliberately NOT 'manage_billing'. That permission exists and would
 * work today, but it is granted only to practice_administrator and
 * billing_administrator, and binding to it would encode the very tier
 * split this new permission exists to replace.
 */
const MANAGE_PATIENT_FINANCIALS = 'manage_patient_financials';

/**
 * May the current user act on a patient's money — collect, refund, apply
 * credit, assign, reverse, remove a recorded payment, void or chase an
 * invoice?
 *
 * One hook, one constant, so the literal is wired in a single place
 * rather than repeated across the surfaces that ask. Reads of balances
 * and billing detail are a different permission and are not gated here:
 * withholding them would hide the ledger rather than the authority.
 */
export const useCanManagePatientFinancials = (): boolean =>
  useCan(MANAGE_PATIENT_FINANCIALS);
